import { isDatePeriod } from './weekNumberUtils';

/**
 * Calculates Year-to-Go (YTG) sum from the period after current period to the end of year
 * @param dataArray - Array of period data values (monthly, quarterly, weekly, etc.)
 * @param currentMonth - Current period index (0-based)
 * @param selectedYear - The year of the forecast data being viewed (optional)
 * @param currentYear - The current calendar year (optional)
 * @returns Sum of values from period after current period to end of year
 */
export const calculateYTG = (
    dataArray: number[] | undefined,
    currentMonth: number | undefined,
    selectedYear?: number,
    currentYear?: number,
    aggregationPeriod?: string
): number => {
    
    if (!dataArray || !Array.isArray(dataArray) || currentMonth === undefined || currentMonth === null) {
        return 0;
    }
    
    const period = aggregationPeriod || 'monthly';
    
    const totalPeriods = period === 'monthly' 
        ? 12 
        : Math.max(0, dataArray.length - 1);
    
    // Convert calendar month to appropriate period index based on aggregation period
    let currentPeriodIndex = currentMonth;
    if (period === 'quarterly') {
        // For quarterly data: convert month (0-11) to quarter (0-3)
        currentPeriodIndex = Math.floor(currentMonth / 3);
    }
    
    if (selectedYear !== undefined && currentYear !== undefined) {
        if (selectedYear < currentYear) {
            return 0;
        } else if (selectedYear > currentYear) {
            let futureYearSum = 0;
            for (let i = 0; i < totalPeriods; i++) {
                futureYearSum += dataArray[i] || 0;
            }
            return futureYearSum;
        }
    }
    
    let ytgSum = 0;
    const summedValues = [];
    for (let i = currentPeriodIndex + 1; i < totalPeriods; i++) {
        const value = dataArray[i] || 0;
        ytgSum += value;
        summedValues.push({ index: i, value });
    }
    
    
    return ytgSum;
};

/**
 * Calculates Year-to-Go (YTG) percentage from the month after current month to the end of year
 * For percentage fields that should be calculated as a ratio of two underlying values
 * @param numeratorArray - Array of numerator values (e.g., grossProfit)
 * @param denominatorArray - Array of denominator values (e.g., grossRevenue)
 * @param currentMonth - Current month index (0-based)
 * @param selectedYear - The year of the forecast data being viewed (optional)
 * @param currentYear - The current calendar year (optional)
 * @returns Percentage calculated as (YTG numerator / YTG denominator) * 100, or 0 if denominator is 0
 */
export const calculateYTGPercentage = (
    numeratorArray: number[] | undefined, 
    denominatorArray: number[] | undefined, 
    currentMonth: number | undefined,
    selectedYear?: number,
    currentYear?: number,
    aggregationPeriod?: string
): number => {
    if (!numeratorArray || !denominatorArray || !Array.isArray(numeratorArray) || !Array.isArray(denominatorArray) || 
        currentMonth === undefined || currentMonth === null) {
        return 0;
    }
    
    const ytgNumerator = calculateYTG(numeratorArray, currentMonth, selectedYear, currentYear, aggregationPeriod);
    const ytgDenominator = calculateYTG(denominatorArray, currentMonth, selectedYear, currentYear, aggregationPeriod);
    
    if (ytgDenominator === 0) return 0;
    
    return Math.round((ytgNumerator / ytgDenominator) * 100 * 100) / 100; // Round to 2 decimal places
};

export const formatValueForClipboard = (value: any): string => {
    if (value === undefined || value === null) return '';
    const roundedValue = Math.round(value * 100) / 100;
    return roundedValue.toString();
};

/**
 * Gets the value to copy for a specific period, preferring actual over forecast when actuals are enabled
 * @param forecastValue - The forecast value
 * @param actualValue - The actual value (may be null/undefined)
 * @param showActuals - Whether actuals mode is enabled
 * @returns The value to copy (actual if available and showActuals is true, otherwise forecast)
 */
export const getValueForCopy = (forecastValue: number | undefined, actualValue: number | null | undefined, showActuals: boolean): number | undefined => {
    if (showActuals && actualValue !== null && actualValue !== undefined) {
        return actualValue;
    }
    return forecastValue;
};

/**
 * Creates week number headers for clipboard copy
 * @param periodNames - Array of period names (could include dates)
 * @param aggregationPeriod - The aggregation period type
 * @returns Array of week numbers or empty strings for non-date periods
 */
export const createWeekNumberHeaders = (periodNames: string[], aggregationPeriod?: string): string[] => {
    if (aggregationPeriod !== 'weekly') {
        return [];
    }
    
    let weekCounter = 0;
    return periodNames.map((period) => {
        if (isDatePeriod(period)) {
            weekCounter++;
            return weekCounter.toString();
        }
        return ''; // For YTG, FY, etc.
    });
};

/**
 * Adds week number row to CSV content if weekly aggregation
 * @param csvContent - Current CSV content
 * @param periodNames - Array of period names
 * @param aggregationPeriod - The aggregation period type
 * @param leadingColumns - Number of leading columns before period columns (default 1 for account/summary tables, 2 for demand table)
 * @returns Updated CSV content with week numbers if applicable
 */
export const addWeekNumberRow = (csvContent: string, periodNames: string[], aggregationPeriod?: string, leadingColumns: number = 1): string => {
    if (aggregationPeriod !== 'weekly') {
        return csvContent;
    }
    
    const weekNumbers = createWeekNumberHeaders(periodNames, aggregationPeriod);
    const leadingTabs = '\t'.repeat(leadingColumns);
    return csvContent + leadingTabs + weekNumbers.join('\t') + '\n';
};

export const getOrderedMonthNames = (startMonth: number): string[] => {
    const baseMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNames = [...baseMonthNames];
    const rotationIndex = (startMonth - 1) % 12;
    return [...monthNames.slice(rotationIndex), ...monthNames.slice(0, rotationIndex)];
};

export interface SummaryTableData {
    totals?: { [key: string]: number[] };
    channels?: Array<{
        channel: string;
        channelTotal?: { [key: string]: number[] };
        customers?: Array<{
            name: string;
            [key: string]: any;
        }>;
    }>;
    currentMonth?: number;
    periodNames?: string[];
}

export const copySummaryTableToClipboard = async (
    summaryData: SummaryTableData,
    view: string,
    startMonth: number,
    selectedYear?: number,
    currentYear?: number,
    aggregationPeriod?: string,
    showActuals?: boolean
): Promise<void> => {
    if (!summaryData || !summaryData.channels || !summaryData.totals) {
        return;
    }

    // Use period names from API response if available, otherwise fall back to month names
    const basePeriodNames = summaryData.periodNames || getOrderedMonthNames(startMonth);
    
    // For clipboard, we need to match the UI: period names (excluding FY) + YTG + FY
    const periodNamesOnly = basePeriodNames.slice(0, -1); // Remove FY from the end
    const headerNames = [...periodNamesOnly, 'YTG', 'FY'];
    
    let csvContent = '';
    csvContent = addWeekNumberRow(csvContent, headerNames, aggregationPeriod, 1);
    csvContent += '\t' + headerNames.map((name: string) => name.toUpperCase()).join('\t') + '\n';

    // Calculate number of periods (excluding FY total)
    const totalArray = summaryData.totals?.[view] || [];
    const periodCount = periodNamesOnly.length; // Use period names length for consistency
    const fyIndex = totalArray.length > 0 ? totalArray.length - 1 : periodCount;

    // Determine actual key based on view
    const actualKey = view === 'units' ? 'actualUnits' : view === 'grossRev' ? 'actualGrossRev' : null;
    const supportsActuals = showActuals && actualKey !== null;
    
    // Get actual array if available
    const totalsActualArray = supportsActuals ? summaryData.totals?.[actualKey] : undefined;
    
    const totalValues = totalArray.slice(0, periodCount).map((value: number, index: number) => {
        const actualValue = totalsActualArray?.[index];
        return formatValueForClipboard(getValueForCopy(value, actualValue, showActuals || false));
    });
    const totalYTG = formatValueForClipboard(calculateYTG(summaryData.totals?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod));
    const totalFYForecast = totalArray[fyIndex];
    const totalFYActual = totalsActualArray?.[fyIndex];
    const totalFY = formatValueForClipboard(getValueForCopy(totalFYForecast, totalFYActual, showActuals || false));
    csvContent += 'TOTAL\t' + totalValues.join('\t') + '\t' + totalYTG + '\t' + totalFY + '\n';

    summaryData.channels?.forEach((channel) => {
        const channelTotalArray = channel.channelTotal?.[view] || [];
        const channelTotalActualArray = supportsActuals ? channel.channelTotal?.[actualKey] : undefined;
        
        const channelTotalValues = channelTotalArray.slice(0, periodCount).map((value: number, index: number) => {
            const actualValue = channelTotalActualArray?.[index];
            return formatValueForClipboard(getValueForCopy(value, actualValue, showActuals || false));
        });
        const channelYTG = formatValueForClipboard(
            view === 'grossProfitPercent'
                ? calculateYTGPercentage(channel.channelTotal?.grossProfit, channel.channelTotal?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod)
                : calculateYTG(channel.channelTotal?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod)
        );
        const channelFYForecast = channelTotalArray[fyIndex];
        const channelFYActual = channelTotalActualArray?.[fyIndex];
        const channelFY = formatValueForClipboard(getValueForCopy(channelFYForecast, channelFYActual, showActuals || false));
        csvContent += channel.channel + '\t' + channelTotalValues.join('\t') + '\t' + channelYTG + '\t' + channelFY + '\n';

        channel.customers?.forEach((customer) => {
            const customerArray = customer?.[view] || [];
            const customerActualArray = supportsActuals ? customer?.[actualKey] : undefined;
            
            const customerValues = customerArray.slice(0, periodCount).map((value: number, index: number) => {
                const actualValue = customerActualArray?.[index];
                return formatValueForClipboard(getValueForCopy(value, actualValue, showActuals || false));
            });
            const customerYTG = formatValueForClipboard(
                view === 'grossProfitPercent'
                    ? calculateYTGPercentage(customer?.grossProfit, customer?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod)
                    : calculateYTG(customer?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod)
            );
            const customerFYForecast = customerArray[fyIndex];
            const customerFYActual = customerActualArray?.[fyIndex];
            const customerFY = formatValueForClipboard(getValueForCopy(customerFYForecast, customerFYActual, showActuals || false));
            csvContent += customer.name + '\t' + customerValues.join('\t') + '\t' + customerYTG + '\t' + customerFY + '\n';
        });
    });

    try {
        await navigator.clipboard.writeText(csvContent);
    } catch (err) {
        console.error('Failed to copy: ', err);
    }
};

export interface PromotionFixedSpend {
    promotionId: number;
    promotionName: string;
    promotionStartDate?: string;
    fixedSpendValues: number[];
}

export interface AccountTableData {
    [key: string]: any;
    currentMonth?: number;
    periodNames?: string[];
    baseUnits?: number[];
    promoUnits?: number[];
    fixedSpendPromotions?: PromotionFixedSpend[];
}

export const copyAccountTableToClipboard = async (
    data: AccountTableData,
    startMonth: number,
    selectedYear?: number,
    currentYear?: number,
    aggregationPeriod?: string,
    showFixedSpendDetails?: boolean,
    showActuals?: boolean
): Promise<void> => {
    if (!data) return;

    // Use period names from API response if available, otherwise fall back to month names
    // Ensure YTG and FY are always included in the headers (matching the UI component logic)
    const basePeriodNames = data.periodNames || getOrderedMonthNames(startMonth);
    const periodNames = basePeriodNames.includes('YTG') && basePeriodNames.includes('FY') 
        ? basePeriodNames 
        : [...(basePeriodNames.filter((name: string) => name !== 'YTG' && name !== 'FY')), 'YTG', 'FY'];
    
    let csvContent = '';
    csvContent = addWeekNumberRow(csvContent, periodNames, aggregationPeriod, 1);
    csvContent += '\t' + periodNames.map((name: string) => name.toUpperCase()).join('\t') + '\n';

    // Calculate number of periods (excluding YTG and FY)
    const basePeriodNamesWithoutYtgFy = basePeriodNames.filter((name: string) => name !== 'YTG' && name !== 'FY');
    const periodCount = basePeriodNamesWithoutYtgFy.length;
    
    // For FY index, check if we have actual data arrays, otherwise use the period count
    const sampleDataArray = data.baseUnits || data.grossRevenue || Object.values(data).find(Array.isArray) || [];
    const fyIndex = sampleDataArray.length > 0 ? sampleDataArray.length - 1 : periodCount;

    // Add Total Units row first (calculated from baseUnits + promoUnits)
    if (data.baseUnits && data.promoUnits) {
        const promoUnits = data.promoUnits;
        const totalUnits = data.baseUnits.map((baseUnit: number, index: number) => baseUnit + (promoUnits[index] || 0));
        const actualTotalUnits = showActuals ? data.actualTotalUnits : undefined;
        
        const values = totalUnits.slice(0, periodCount).map((value: number, index: number) => {
            const actualValue = actualTotalUnits?.[index];
            return formatValueForClipboard(getValueForCopy(value, actualValue, showActuals || false));
        });
        const ytgValue = formatValueForClipboard(calculateYTG(totalUnits, data.currentMonth, selectedYear, currentYear, aggregationPeriod));
        const fyForecast = totalUnits[fyIndex];
        const fyActual = actualTotalUnits?.[fyIndex];
        const fyValue = formatValueForClipboard(getValueForCopy(fyForecast, fyActual, showActuals || false));
        csvContent += 'Total Units\t' + values.join('\t') + '\t' + ytgValue + '\t' + fyValue + '\n';
    }

    // Add other data rows
    Object.keys(data).forEach((key) => {
        if (!Array.isArray(data[key])) return;
        
        if (key === 'periodNames' || key === 'fixedSpendPromotions' || key === 'actualGrossRevenue' || key === 'actualTotalUnits') return;

        const rowName = key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase());
        
        // Check if this row supports actuals
        const supportsActuals = showActuals && (key === 'grossRevenue');
        const actualArray = supportsActuals ? data.actualGrossRevenue : undefined;
        
        const values = data[key].slice(0, periodCount).map((value: number, index: number) => {
            const actualValue = actualArray?.[index];
            return formatValueForClipboard(getValueForCopy(value, actualValue, showActuals || false));
        });
        const ytgValue = formatValueForClipboard(calculateYTG(data[key], data.currentMonth, selectedYear, currentYear, aggregationPeriod));
        const fyForecast = data[key][fyIndex];
        const fyActual = actualArray?.[fyIndex];
        const fyValue = formatValueForClipboard(getValueForCopy(fyForecast, fyActual, showActuals || false));
        csvContent += rowName + '\t' + values.join('\t') + '\t' + ytgValue + '\t' + fyValue + '\n';
        
        if (key === 'fixedSpend' && showFixedSpendDetails && data.fixedSpendPromotions) {
            data.fixedSpendPromotions.forEach((promo: PromotionFixedSpend) => {
                const promoValues = promo.fixedSpendValues.slice(0, periodCount).map((value: number) => formatValueForClipboard(value));
                const promoYtgValue = formatValueForClipboard(calculateYTG(promo.fixedSpendValues, data.currentMonth, selectedYear, currentYear, aggregationPeriod));
                const promoFyIndex = promo.fixedSpendValues.length > 0 ? promo.fixedSpendValues.length - 1 : periodCount;
                const promoFyValue = formatValueForClipboard(promo.fixedSpendValues[promoFyIndex]);
                csvContent += promo.promotionName + '\t' + promoValues.join('\t') + '\t' + promoYtgValue + '\t' + promoFyValue + '\n';
            });
        }
    });

    try {
        await navigator.clipboard.writeText(csvContent);
    } catch (err) {
        console.error('Failed to copy: ', err);
    }
};

export interface DemandTableData {
    periodicDemands?: Array<{
        skuNumber?: string;
        skuDescription?: string;
        units?: { [week: string]: { totalUnits: number; weekMonth: number } };
    }>;
    weekToMonthMap?: { [week: string]: number };
}

export const copyDemandTableToClipboard = async (
    summaryData: DemandTableData,
    aggregationPeriod?: string
): Promise<void> => {
    if (!summaryData || !summaryData.periodicDemands || !summaryData.weekToMonthMap) {
        return;
    }

    const weeks = Object.keys(summaryData.weekToMonthMap);
    const allColumns = ['SKU', 'Description', ...weeks, 'FY Total'];
    
    let csvContent = '';
    csvContent = addWeekNumberRow(csvContent, weeks, aggregationPeriod, 2);
    csvContent += 'SKU\tDescription\t' + weeks.join('\t') + '\tFY Total\n';

    summaryData.periodicDemands.forEach((demand) => {
        const skuNumber = demand.skuNumber || 'Unknown SKU';
        const skuDescription = demand.skuDescription || 'Unknown SKU';
        const units = demand.units || {};

        const weekValues = weeks.map(week => {
            const weekData = units[week];
            const totalUnits = weekData ? weekData.totalUnits : 0;
            return formatValueForClipboard(totalUnits);
        });

        const fyTotal = Object.values(units).reduce((sum, weekData) => sum + (weekData?.totalUnits || 0), 0);

        csvContent += `${skuNumber}\t${skuDescription}\t${weekValues.join('\t')}\t${formatValueForClipboard(fyTotal)}\n`;
    });

    try {
        await navigator.clipboard.writeText(csvContent);
    } catch (err) {
        console.error('Failed to copy: ', err);
    }
};
